type LegacyAudioGlobal = typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

const clamp = (value: number, min = 0, max = 1): number => {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
};

/** Lightweight procedural soundscape built entirely with the Web Audio API. */
export class SynthAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private engineOscillator: OscillatorNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private engineGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private disposed = false;

  public get isStarted(): boolean {
    return this.context !== null && this.context.state !== "closed";
  }

  /**
   * Creates or resumes the AudioContext. Call this directly from a user input
   * handler so browser autoplay policies can grant audio playback.
   */
  public async start(): Promise<void> {
    if (this.disposed) return;
    let pendingContext: AudioContext | null = null;
    try {
      if (!this.context) {
        const audioGlobal = globalThis as LegacyAudioGlobal;
        const AudioContextConstructor = audioGlobal.AudioContext ?? audioGlobal.webkitAudioContext;
        if (!AudioContextConstructor) return;

        const context = new AudioContextConstructor({ latencyHint: "interactive" });
        pendingContext = context;
        const master = context.createGain();
        const compressor = context.createDynamicsCompressor();
        master.gain.value = 0.32;
        compressor.threshold.value = -14;
        compressor.knee.value = 12;
        compressor.ratio.value = 6;
        compressor.attack.value = 0.003;
        compressor.release.value = 0.22;
        master.connect(compressor);
        compressor.connect(context.destination);

        this.context = context;
        this.master = master;
        this.noiseBuffer = this.createNoiseBuffer(context, 2);
        this.createEngineVoice(context, master);
      }

      if (this.context.state === "suspended") {
        await this.context.resume();
      }
    } catch {
      this.resetAudioGraph(pendingContext);
    }
  }

  /** `speed` accepts either a normalized 0–1 value or world speed up to ~28. */
  public updateEngine(speed: number, boost: boolean): void {
    const context = this.context;
    const oscillator = this.engineOscillator;
    const filter = this.engineFilter;
    const gain = this.engineGain;
    if (!context || !oscillator || !filter || !gain) return;

    const absoluteSpeed = Math.abs(Number.isFinite(speed) ? speed : 0);
    const normalizedSpeed = clamp(absoluteSpeed > 1 ? absoluteSpeed / 28 : absoluteSpeed);
    const boostLift = boost ? 1 : 0;
    const now = context.currentTime;
    oscillator.frequency.setTargetAtTime(46 + normalizedSpeed * 74 + boostLift * 28, now, 0.055);
    filter.frequency.setTargetAtTime(260 + normalizedSpeed * 720 + boostLift * 520, now, 0.06);
    filter.Q.setTargetAtTime(1.2 + normalizedSpeed * 2.2, now, 0.08);
    gain.gain.setTargetAtTime(0.018 + normalizedSpeed * 0.055 + boostLift * 0.035, now, 0.05);
  }

  public rifle(): void {
    const variation = 0.94 + Math.random() * 0.12;
    this.tone(210 * variation, 58, 0.095, 0.24, "square");
    this.noise(0.075, 0.11, 2800, "bandpass");
  }

  public missile(): void {
    this.tone(74, 38, 0.28, 0.2, "sawtooth");
    this.tone(125, 310, 0.38, 0.08, "triangle", 0.025);
    this.noise(0.42, 0.13, 1050, "lowpass");
  }

  public melee(): void {
    this.tone(1280, 185, 0.14, 0.13, "sawtooth");
    this.tone(96, 54, 0.18, 0.16, "triangle", 0.015);
    this.noise(0.09, 0.075, 4100, "highpass");
  }

  /**
   * Surface/legacy impact: shots stopped by structures and other blunt,
   * non-scoring contacts. This used to be the ONLY hit sound in the game —
   * rifle hits, incoming fire and cover strikes were all this one thud, which
   * was on the request's evidence list. Scoring hits now route through
   * `weaponHit` / `playerHit` and this stays as the dull "you hit concrete".
   */
  public impact(): void {
    this.tone(105, 42, 0.17, 0.19, "triangle");
    this.noise(0.16, 0.14, 760, "lowpass");
  }

  /**
   * A landed hit on an enemy, voiced by weapon so an unlabelled recording can
   * be told apart by ear alone:
   *   rifle   — short metallic tick, almost dry
   *   missile — low thump with a mid crack
   *   blade   — falling shear plus a metal ring that hangs on
   * `direct` (ACS-overload ×1.65) layers a bright crack on top of any of them.
   */
  public weaponHit(weapon: "rifle" | "missile" | "blade", direct: boolean): void {
    if (weapon === "rifle") {
      this.tone(2600, 1750, 0.05, 0.1, "square");
      this.noise(0.035, 0.07, 5200, "highpass");
    } else if (weapon === "missile") {
      this.tone(96, 36, 0.26, 0.28, "sine");
      this.tone(320, 130, 0.12, 0.12, "triangle");
      this.noise(0.3, 0.17, 880, "lowpass");
    } else {
      this.tone(1500, 240, 0.16, 0.22, "sawtooth");
      this.tone(880, 830, 0.36, 0.1, "triangle", 0.02);
      this.tone(64, 38, 0.24, 0.2, "sine");
      this.noise(0.12, 0.1, 3600, "bandpass");
    }
    if (direct) {
      this.tone(3400, 2300, 0.07, 0.13, "square", 0.012);
      this.noise(0.05, 0.09, 6400, "highpass", 0.012);
    }
  }

  /**
   * An ACS breaking: everything else dips for a beat, then a falling zap over
   * a sub drop. The duck is what makes the moment read as an event instead of
   * one more spark noise in the mix.
   */
  public staggerBreak(): void {
    this.duck(0.34, 0.5);
    this.tone(980, 105, 0.3, 0.26, "sawtooth");
    this.tone(130, 27, 0.42, 0.3, "sine", 0.03);
    this.noise(0.2, 0.12, 2400, "bandpass", 0.05);
  }

  /** Kill, by weight class: skirmishers pop, the bulwark's frame lets go with
   * a deeper second charge and trailing crackles. */
  public kill(heavy: boolean): void {
    this.explosion();
    if (heavy) {
      this.tone(36, 21, 1.15, 0.28, "sine", 0.16);
      this.noise(0.32, 0.14, 1500, "bandpass", 0.24);
      this.noise(0.5, 0.12, 900, "lowpass", 0.42);
    }
  }

  /**
   * Taking a hit, scaled by how bad it was. A graze is an interior thud; a
   * heavy or direct hit adds a low body and an alarm-adjacent blip so the ear
   * gets the same information the shaking camera carries.
   */
  public playerHit(heavy: boolean, direct: boolean): void {
    this.tone(130, 44, 0.2, 0.26, "triangle");
    this.noise(0.15, 0.18, 520, "lowpass");
    if (heavy) {
      this.tone(72, 28, 0.32, 0.22, "sawtooth", 0.015);
      this.tone(540, 470, 0.1, 0.1, "square", 0.09);
    }
    if (direct) this.tone(2900, 2100, 0.07, 0.11, "square", 0.02);
  }

  /** Briefly pull the master down and let it recover: a mix-wide accent. */
  private duck(depth: number, recoverSeconds: number): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master || context.state === "closed") return;
    const now = context.currentTime;
    const resting = 0.32;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(Math.max(0.0001, resting * (1 - depth)), now);
    master.gain.setTargetAtTime(resting, now + 0.05, recoverSeconds / 3);
  }

  public explosion(): void {
    this.tone(78, 24, 0.78, 0.3, "sawtooth");
    this.tone(46, 29, 0.95, 0.17, "sine", 0.035);
    this.noise(1.05, 0.29, 620, "lowpass");
    this.noise(0.36, 0.1, 2300, "bandpass", 0.04);
  }

  public alarm(): void {
    this.tone(680, 680, 0.12, 0.1, "square");
    this.tone(510, 510, 0.15, 0.09, "square", 0.17);
    this.tone(680, 680, 0.12, 0.1, "square", 0.37);
  }

  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const context = this.context;
    const now = context?.currentTime ?? 0;

    if (this.engineGain && context) {
      this.engineGain.gain.cancelScheduledValues(now);
      this.engineGain.gain.setValueAtTime(Math.max(0.0001, this.engineGain.gain.value), now);
      this.engineGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
    }
    if (this.engineOscillator) {
      try {
        this.engineOscillator.stop(now + 0.035);
      } catch {
        // The oscillator may already have ended while the page is unloading.
      }
    }

    this.engineOscillator = null;
    this.engineFilter = null;
    this.engineGain = null;
    this.noiseBuffer = null;
    this.master?.disconnect();
    this.master = null;
    this.context = null;
    if (context && context.state !== "closed") {
      void context.close().catch(() => undefined);
    }
  }

  private createEngineVoice(context: AudioContext, destination: AudioNode): void {
    const oscillator = context.createOscillator();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    oscillator.type = "sawtooth";
    oscillator.frequency.value = 46;
    filter.type = "lowpass";
    filter.frequency.value = 260;
    filter.Q.value = 1.2;
    gain.gain.value = 0.018;
    oscillator.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    oscillator.start();
    this.engineOscillator = oscillator;
    this.engineFilter = filter;
    this.engineGain = gain;
  }

  private resetAudioGraph(fallbackContext: AudioContext | null = null): void {
    const context = this.context ?? fallbackContext;
    if (this.engineOscillator) {
      try {
        this.engineOscillator.stop();
      } catch {
        // A partially-created voice may already have been stopped.
      }
      this.engineOscillator.disconnect();
    }
    this.engineFilter?.disconnect();
    this.engineGain?.disconnect();
    this.master?.disconnect();
    this.engineOscillator = null;
    this.engineFilter = null;
    this.engineGain = null;
    this.noiseBuffer = null;
    this.master = null;
    this.context = null;
    if (context && context.state !== "closed") {
      void context.close().catch(() => undefined);
    }
  }

  private createNoiseBuffer(context: AudioContext, seconds: number): AudioBuffer {
    const frameCount = Math.ceil(context.sampleRate * seconds);
    const buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const samples = buffer.getChannelData(0);
    let previous = 0;
    for (let index = 0; index < frameCount; index += 1) {
      const white = Math.random() * 2 - 1;
      previous = previous * 0.18 + white * 0.82;
      samples[index] = previous;
    }
    return buffer;
  }

  private tone(
    startFrequency: number,
    endFrequency: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    delay = 0,
  ): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master || context.state === "closed") return;
    const startAt = context.currentTime + Math.max(0, delay);
    const stopAt = startAt + Math.max(0.025, duration);
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(1, startFrequency), startAt);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), stopAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), startAt + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
    oscillator.connect(gain);
    gain.connect(master);
    oscillator.addEventListener(
      "ended",
      () => {
        oscillator.disconnect();
        gain.disconnect();
      },
      { once: true },
    );
    oscillator.start(startAt);
    oscillator.stop(stopAt + 0.01);
  }

  private noise(
    duration: number,
    volume: number,
    frequency: number,
    filterType: BiquadFilterType,
    delay = 0,
  ): void {
    const context = this.context;
    const master = this.master;
    const buffer = this.noiseBuffer;
    if (!context || !master || !buffer || context.state === "closed") return;
    const safeDuration = Math.min(buffer.duration, Math.max(0.025, duration));
    const startAt = context.currentTime + Math.max(0, delay);
    const stopAt = startAt + safeDuration;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer;
    filter.type = filterType;
    filter.frequency.value = Math.max(20, frequency);
    filter.Q.value = filterType === "bandpass" ? 1.4 : 0.7;
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), startAt + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(master);
    source.addEventListener(
      "ended",
      () => {
        source.disconnect();
        filter.disconnect();
        gain.disconnect();
      },
      { once: true },
    );
    source.start(startAt, Math.random() * Math.max(0, buffer.duration - safeDuration), safeDuration);
    source.stop(stopAt + 0.01);
  }
}
